import axios from 'axios';
import {env} from '../env';
import {navigate} from "../navigation/navigationHistory";
import { encryptToken, decryptToken } from '../utils/encrypt';

export let attributeStorage = sessionStorage;

const attributeKeys = ['postalCode', 'phoneNumber', 'emailAddress', 'birthDate'];
const QUOTE_KEY = 'quoteOrAppNumber';

let attributesAllowed = ['postalCode', 'phoneNumber', 'emailAddress', 'birthDate'];

const baseUrl = env.apiBaseUrl;

let baseConfig = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
};


const prefixedURL = `${baseUrl}/coreapi/v5`;
export const api = axios.create({
  baseURL: prefixedURL,
  ...baseConfig
});

export const anonApi = axios.create({
  baseURL: prefixedURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
});

export const apiAbs = axios.create({
  baseURL: baseUrl,
  ...baseConfig
});

export const setAllowedAttributes = (attrList) => {
  if (attrList) {
    let possibleAttrs = [ ];
    for (const attr of attrList) {
      if (attributeKeys.includes(attr)) {
        possibleAttrs.push(attr);
      }
    }
    if (possibleAttrs) {
      attributesAllowed = possibleAttrs;
    }
  }
}

export const setSessionLinkIds = (postalCode, emailAddress, phoneNumber, birthDate) => {
  setAttributeValue('postalCode', postalCode);
  setAttributeValue('emailAddress', emailAddress);
  setAttributeValue('phoneNumber', phoneNumber);
  setAttributeValue('birthDate', birthDate);
}

export const setQouteOrAppNumber = (appNumber) => {
  setAttributeValue(QUOTE_KEY, appNumber)
}

function setAttributeValue(key, value) {
  // NOTE: sessionStorage converts null values to "null" text.
  if ((value == null) || (value == undefined)) {
    value = '';
  }
  if ((key === QUOTE_KEY) || attributesAllowed.includes(key)) {
    attributeStorage[key] = value;  // set value to space or non-empty text
  }
}

function addAttribute(obj, key) {
  obj[key] = attributeStorage[key];
}

function getReadyAttributes( ) {
  const attributes = (areAttributesReady() ? {} : null);
  if (attributes != null) {
    for (const key of attributeKeys) {
      if ((key != 'birthDate') || attributesAllowed.includes('birthDate')) {
        addAttribute(attributes, key);
      }
    }
  }
  return (attributes);
}

function areAttributesReady( ) {
  if (attributeStorage[QUOTE_KEY]) {
    for (const key of attributeKeys) {
      if (attributeStorage[key]) return (true);  // not undefined, null, or empty string
    }
  }
  return (false);
}

export const resetSessionLinkIds = (eraseQuoteNumber) => {
  for (const key of attributeKeys) {
    attributeStorage[key] = '';
  }
  if (eraseQuoteNumber) {
    attributeStorage[QUOTE_KEY] = '';
  }
}

function getCurrentToken() {
  const encryptToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1];
  if (encryptToken == null) {
    return null;
  }
  return decryptToken(encryptToken);
}

function setCurrentToken(token) {
  if (token === null) {
    document.cookie=`token=${token}; SameSite=None; Secure; path=/`;
  } else {
    const encryptedToken = encryptToken(token);
    document.cookie=`token=${encryptedToken}; SameSite=None; Secure; path=/`;
  }
}

let waitingQueue = null;

function releaseWaiters( ) {
  waitingQueue.forEach(onJwt => onJwt(jwtToken));
  waitingQueue = null;
}

async function sleep(time) {
  await new Promise(resolve => setTimeout(resolve, time));
}

async function getToken() {
  const currentToken = getCurrentToken();
  if (currentToken) {
    return Promise.resolve(currentToken);
  } else {
    if (waitingQueue === null) {
      waitingQueue = [];
      return anonApi.post('/clients/ConsumerSalesPortal/sessions').then(async res => {
        const jwtToken = res.data.jwtToken || null;
        setCurrentToken(jwtToken);
        if (jwtToken && areAttributesReady()) {
          const quoteOrAppNumber = attributeStorage[QUOTE_KEY];
          await sleep(250);  // TODO: investigate why this is needed when clocks are auto-sync'd
          return api.put('/applications/updateSessionLinkRequest', getReadyAttributes(), { params: {quoteOrAppNumber} })
              .then(response => {
                console.log("Session link updated: " + response.status);
                releaseWaiters();
                return jwtToken
              });
        } else {
          releaseWaiters();
          return jwtToken
        }
      }).catch(err => {
        console.log(err);
        setCurrentToken(null);
        releaseWaiters();
        err._jwtInitiation = true;
        throw err;
      })
    } else {
      return new Promise((resolve, reject) => {
        waitingQueue.push(token => {
          if (token === null) {
            reject(token);
          } else {
            resolve(token);
          }
        })
      });
    }
  }
}

async function replaceJwt(error) {
  const originalRequest = error.config;
  const statusCode = (error.response ? error.response.status : 0);
  if (statusCode === 401 && !error._jwtInitiation && !originalRequest._retry) {
    console.log('session expired, retrying');

    //invalidate current token
    setCurrentToken(null);
    originalRequest._retry = true;
    let prom = getToken();
    let token = await prom;
    if (!token) {
      throw new Error('no jwt, request canceled ' + originalRequest.method + ' ' + originalRequest.url);
    }
    originalRequest.headers.Authorization = 'Bearer ' + token;
    return axios(originalRequest);
  } else if (statusCode === 503) {
    console.log('Server detected in maintenance mode.  Redirecting to offline advisory.');
    Promise.reject(error);
    navigate('/server-maintenance');
  } else {
    return Promise.reject(error);
  }
}

function attachInterceptor(instance) {
  instance.interceptors.request.use((config) => {
    return getToken().then((token) => {
      if (!token) {
        throw new Error('no jwt, request canceled ' + config.method + ' ' + config.url);
      }
      config.headers.Authorization = 'Bearer ' + token;
      return Promise.resolve(config);
    })
  }, (error) => {
    console.log("Unable to get security token: " + (error.response ? error.response.status : error.message));
    return Promise.reject(error);
  });

  //handling expired tokens
  instance.interceptors.response.use(response => response, replaceJwt);
}

attachInterceptor(api);
attachInterceptor(apiAbs);
